import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { Observable, firstValueFrom, map, take } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { CalculateDistanceGQL, CalculateDistanceOutput, DispatchReportJobFragment, ListDispatchReportJobsGQL } from '../../../../../generated/graphql.generated';
import { dayjs } from '../../../../core/dayjs/dayjs';
import { EventAttendeeRoles } from '../../../../global.constants';
import { BrandingService } from '../../../../services/branding.service';
import { TimezoneHelperService } from '../../../../services/timezone-helper.service';
import { selectDistanceUnit } from '../../../../state/branding.store';
import { DispatchEvent, DispatchFeature, PotentialCrewMember } from '../../store/dispatch.reducer';
import { selectDerivedEvents, selectEnrichedCrew, selectPotentialAssets, selectTodaysRevenue } from '../../store/dispatch.selectors';

const selectReportMetrics = createSelector(
  selectTodaysRevenue,
  selectEnrichedCrew,
  selectPotentialAssets,
  (financials, crew, assets) => {
    const isActive = (c: PotentialCrewMember) => (c.user.available === true && !!c.user.rateHourly) || c.isAssigned;
    const crewAssigned = crew.filter(c => isActive(c) && c.isAssigned).length;
    const totalCrew = crew.filter(c => isActive(c) && !c.isUnavailable && !c.isBookedOff).length;
    const trucks = assets.filter(a => ['Truck', 'Delivery'].includes(a.type));
    const trucksAssigned = trucks.filter(t => t.eventsCount > 0).length;
    const totalTrucks = trucks.length;
    return {
      revenue: financials.total / 100,
      laborCost: financials.totalLaborCost / 100,
      laborPercentage: financials.laborCostPercentage as string,
      totalCrew,
      crewAssigned,
      crewUtilization: totalCrew ? Math.round((crewAssigned / totalCrew) * 100) : 0,
      totalTrucks,
      trucksAssigned,
      truckUtilization: totalTrucks ? Math.round((trucksAssigned / totalTrucks) * 100) : 0,
    };
  },
);
import {
  convertMetersToDisplayUnit,
  formatArrivalWindow,
  formatDuration,
  formatInventoryVolume,
  formatInventoryWeight,
  formatTravelTime,
  getLocationLabel,
  getOrderedLocationIdsForCalculation,
  getOrderedLocationsForDisplay,
  parseCrewSummary,
  parseInventory,
} from '../../utils/dispatch-report.utils';

interface ReportMetrics {
  revenue: number;
  laborCost: number;
  laborPercentage: string;
  totalCrew: number;
  crewAssigned: number;
  crewUtilization: number;
  totalTrucks: number;
  trucksAssigned: number;
  truckUtilization: number;
}

interface FormattedDispatchEvent extends DispatchEvent {
  // Pre-computed display values to avoid template method calls
  formattedArrivalWindow: string;
  formattedDuration: string;
  formattedTravelTime: string;
  displayMileage: number;
  distanceUnit: string;
  inventoryWeight: string;
  inventoryVolume: string;
  crewSummaryParagraphs: string[];
  locationsToShow: any[];
  /** Additional contacts of the job's customer (name/email/phone/relationship) */
  additionalContacts: { name?: string; email?: string; phone?: string; relationship?: string }[];
}

/** Event types that render in the OPS section (above job routes) */
const OPS_SECTION_EVENT_TYPES = ['ops-manager', 'reserve', 'non-billable', 'ops-task'];

interface ReportRoute {
  id: string;
  routeName: string;
  truckId: string;
  crewLead: { name: string; phone: string };
  jobs: FormattedDispatchEvent[];
  routeDate: Date | null;
}

@Component({
  selector: 'app-dispatch-operations-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dispatch-operations-report.component.html',
  styleUrls: ['./dispatch-operations-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DispatchOperationsReportComponent implements OnInit {
  @Output() closeReport = new EventEmitter<void>();

  @HostListener('document:keydown.escape', ['$event']) 
  onKeydownHandler(_event: KeyboardEvent) {
    this.handleClose(); 
  }

  currentDate = new Date();
  
  metrics$: Observable<ReportMetrics>;
  zoneName$: Observable<string>;
  distanceUnit$: Observable<'metric' | 'imperial'>;
  
  // Pre-computed routes with all formatted values (populated after data loads)
  formattedRoutes: ReportRoute[] = [];

  /** OPS-section events (ops-manager / reserve / admin / ops-task), rendered ABOVE job routes */
  formattedOpsEvents: FormattedDispatchEvent[] = [];
  
  jobDetailsMap = new Map<string, DispatchReportJobFragment>();
  
  // Map of jobId -> calculated distance output from CalculateDistanceGQL
  calculatedDistancesMap = new Map<string, CalculateDistanceOutput>();
  
  // Distance unit preference (metric or imperial) from system settings
  distanceUnits: 'metric' | 'imperial' = environment.defaultUnits as 'metric' | 'imperial';

  // Timezone for formatting arrival windows and durations
  private timezone: string;

  // Loading state for async data fetching
  isLoading = true;
  loadingMessage = 'Loading report data...';

  constructor(
    private store: Store,
    private brandingService: BrandingService,
    private listDispatchReportJobsGQL: ListDispatchReportJobsGQL,
    private calculateDistanceGQL: CalculateDistanceGQL,
    private cdr: ChangeDetectorRef,
    private timezoneHelper: TimezoneHelperService
  ) {}

  async ngOnInit() {
    // Set up reactive observable for template display
    this.distanceUnit$ = this.store.select(selectDistanceUnit);

    // Await the value before proceeding, ensuring distanceUnits is set
    // before loadAllReportData() calls calculateJobDistancesAsync()
    this.distanceUnits = await firstValueFrom(this.distanceUnit$.pipe(take(1)));

    // Get timezone for formatting arrival windows and durations
    this.timezone = this.timezoneHelper.getCurrentTimezone();

    // Header date must be the DISPATCH DATE being viewed, not today — a
    // report for tomorrow printed today would otherwise carry today's date.
    this.currentDate = await firstValueFrom(
      this.store.select(DispatchFeature.selectDispatchDate).pipe(take(1)),
    ) || new Date();
    
    this.zoneName$ = this.brandingService.currentZone().pipe(
      map(zone => zone?.name || 'MOVERIGHT')
    );

    this.metrics$ = this.store.select(selectReportMetrics);

    // Fetch all job data before showing the report
    await this.loadAllReportData();
  }

  /**
   * Requirement: Preload report data before rendering to ensure accurate
   * mileage, duration, and job details are displayed when print dialog opens.
   */
  private async loadAllReportData(): Promise<void> {
    this.isLoading = true;
    this.loadingMessage = 'Fetching job details...';
    this.cdr.markForCheck();

    try {
      // Get all events from the store
      const events = await firstValueFrom(this.store.select(selectDerivedEvents).pipe(take(1)));
      
      // Collect all job IDs
      const jobIds = [...new Set(
        events
          .map(e => e.event.job?.id)
          .filter((id): id is string => !!id)
      )];

      // Fetch all job details in a single batch query
      this.loadingMessage = `Fetching details for ${jobIds.length} jobs...`;
      this.cdr.markForCheck();
      
      await this.fetchJobDetailsBatch(jobIds);

      // Calculate distances for all jobs in parallel
      this.loadingMessage = 'Calculating distances...';
      this.cdr.markForCheck();
      
      await Promise.all(
        jobIds
          .filter(id => this.jobDetailsMap.has(id))
          .map(id => this.calculateJobDistancesAsync(id, this.jobDetailsMap.get(id)!))
      );

      // Build formatted routes with all pre-computed values
      const { routes, opsEvents } = this.buildFormattedRoutes(events);
      this.formattedRoutes = routes;
      this.formattedOpsEvents = opsEvents;

      this.isLoading = false;
      this.cdr.markForCheck();

      // Small delay to ensure the view is rendered, then print
      setTimeout(() => {
        this.handlePrint();
      }, 100);

    } catch (error) {
      console.error('Error loading report data:', error);
      this.isLoading = false;
      this.loadingMessage = 'Error loading data. You can still print the report.';
      this.cdr.markForCheck();
    }
  }

  /**
   * Fetch multiple job details in a single batch query
   */
  private async fetchJobDetailsBatch(jobIds: string[]): Promise<void> {
    if (jobIds.length === 0) return;

    // Filter out already-fetched jobs
    const idsToFetch = jobIds.filter(id => !this.jobDetailsMap.has(id));
    if (idsToFetch.length === 0) return;

    try {
      const result = await firstValueFrom(
        this.listDispatchReportJobsGQL.fetch({
          jobIds: idsToFetch,
          resolve: ['locations']
        })
      );
      const jobs = result.data?.jobs?.jobs || [];
      jobs.forEach(job => {
        if (job) {
          this.jobDetailsMap.set(job.id, job as DispatchReportJobFragment);
        }
      });
    } catch (e) {
      console.error('Failed to fetch job details:', e);
    }
  }

  /**
   * Calculate distances asynchronously (Promise-based)
   */
  private async calculateJobDistancesAsync(
    jobId: string,
    job: DispatchReportJobFragment
  ): Promise<CalculateDistanceOutput | null> {
    if (this.calculatedDistancesMap.has(jobId)) {
      return this.calculatedDistancesMap.get(jobId)!;
    }

    const locationIds = getOrderedLocationIdsForCalculation(job.locations || []);
    if (locationIds.length < 2) return null;

    try {
      const result = await firstValueFrom(
        this.calculateDistanceGQL.fetch({ locationIds, units: this.distanceUnits })
      );
      const distanceOutput = result.data?.calculateDistance;
      if (distanceOutput) {
        this.calculatedDistancesMap.set(jobId, distanceOutput);
        return distanceOutput;
      }
    } catch (e) {
      console.error(`Failed to calculate distances for job ${jobId}:`, e);
    }
    return null;
  }

  /**
   * Build formatted routes with all pre-computed display values.
   * This eliminates template method calls that would run on every change detection cycle.
   * OPS section (ops-manager/reserve/non-billable/ops-task events) is returned
   * separately and rendered ABOVE the job routes.
   */
  private buildFormattedRoutes(events: DispatchEvent[]): { routes: ReportRoute[]; opsEvents: FormattedDispatchEvent[] } {
    const routeMap = new Map<string, ReportRoute>();
    const distanceUnit = this.distanceUnits === 'metric' ? 'km' : 'mi';
    const opsEvents: FormattedDispatchEvent[] = [];

    // Sort events by start time first
    const sortedEvents = [...events].sort((a, b) => a.event.start - b.event.start);

    sortedEvents.forEach(event => {
      const isOpsSection = OPS_SECTION_EVENT_TYPES.includes(event.event.type);
      const job = event.event.job?.id ? this.jobDetailsMap.get(event.event.job.id) : undefined;
      const calcResult = event.event.job?.id ? this.calculatedDistancesMap.get(event.event.job.id) : undefined;
      const inventory = parseInventory(job);

      const eventLocations = (event.event as any).locations || [];
      const jobLocations = job?.locations || [];
      const locationsToShow = getOrderedLocationsForDisplay(eventLocations, jobLocations)
        .map(loc => ({
          ...loc,
          formattedLabel: getLocationLabel(loc.type)
        }));

      const additionalContacts = (event.customer?.user as any)?.additionalContacts || [];

      const formattedEvent: FormattedDispatchEvent = {
        ...event,
        formattedArrivalWindow: formatArrivalWindow(event.event.start, this.timezone),
        formattedDuration: formatDuration(event.event.start, event.event.end, this.timezone),
        formattedTravelTime: formatTravelTime(Math.round((calcResult?.estimatedTime || 0) / 60)),
        displayMileage: convertMetersToDisplayUnit(calcResult?.totalDistance || 0, distanceUnit as 'km' | 'mi'),
        distanceUnit,
        inventoryWeight: formatInventoryWeight(inventory),
        inventoryVolume: formatInventoryVolume(inventory),
        crewSummaryParagraphs: parseCrewSummary(event.event.job?.crewSummary),
        locationsToShow,
        additionalContacts,
      };

      // OPS-section events go to their own list — never into a truck route
      if (isOpsSection) {
        opsEvents.push(formattedEvent);
        return;
      }

      // If no truck assigned, group under "Unassigned"
      const truckName = event.trucks.length > 0 ? event.trucks.map(t => t.name).join(', ') : 'Unassigned';
      const truckId = event.trucks.length > 0 ? event.trucks[0].name : 'N/A';

      if (!routeMap.has(truckName)) {
        // Find crew lead
        const crewLead = event.crew.find(c => c.role === EventAttendeeRoles.crewLead);
        // Get route date from first event
        const routeDate = event.event.start
          ? dayjs.tz(Number(event.event.start) * 1000, this.timezone).toDate()
          : null;

        routeMap.set(truckName, {
          id: truckName,
          routeName: `Route - ${truckName}`,
          truckId: truckId,
          crewLead: {
            name: crewLead ? crewLead.name : 'No Lead',
            phone: crewLead?.user?.phone || ''
          },
          jobs: [],
          routeDate
        });
      }

      routeMap.get(truckName)!.jobs.push(formattedEvent);
    });

    // Convert map to array and sort by route name (truck name)
    const routes = Array.from(routeMap.values()).sort((a, b) => {
      if (a.id === 'Unassigned') return 1;
      if (b.id === 'Unassigned') return -1;
      return a.routeName.localeCompare(b.routeName);
    });

    return { routes, opsEvents };
  }

  // Keep old methods for backward compatibility but they're no longer primary
  fetchJobDetails(_events: DispatchEvent[]) {
    // This is now handled by loadAllReportData
  }

  handlePrint() {
    window.print();
  }

  /**
   * Format phone number for display, with fallback for missing phone
   */
  formatPhone(phone: string | null | undefined): string {
    return phone || 'Phone not provided';
  }

  handleClose() {
    this.closeReport.emit();
  }
}
